/**
 * Dashboard Service
 * Handles dashboard metrics and statistics API calls
 */
import BaseApiService from '../base/BaseApiService';
import type {
  User,
  Assessment,
  AssessmentsResponse,
  SuppliersResponse,
  ContractsResponse,
  GapItem,
  EvidenceStatistics,
  ExecutiveDashboardData,
  ContractManagerDashboardResponse,
  ProgramLeadMetricsData,
  ProgramLeadQuickActionsData,
  AdminQuickActionsData,
  AssessorMetricsData,
  AssessorQuickActionsData,
  SchedulerMetricsData,
  SchedulerQuickActionsData,
  AssessorLoadingData,
  ContractRoleMetricsData,
  ContractRoleQuickActionsData,
  GapItemsFilters,
  WidgetData
} from '@app-types';
import { canUserAccessAssessments } from '@utils/permissionUtils';
import { getBrowserTimeZone } from '@utils/dateUtils';

// The server groups Meetings into days and months in this zone, so the chart agrees with the Meeting Dates the lists show.
const assessorLoadingPath = (basePath: string, month: string): string =>
  `${basePath}/assessor-loading?month=${month}&timezone=${encodeURIComponent(getBrowserTimeZone())}`;

class DashboardService extends BaseApiService {
  /**
   * Get dashboard metrics - Optimized to make only 3 API calls instead of 4
   * @param user - The authenticated user
   */
  async getDashboardMetrics(user: User): Promise<{
    assessments: { total: number; byStatus: Array<{ status: string; count: number }> };
    suppliers: { total: number };
    contracts: { total: number };
    recentAssessments: Assessment[];
  }> {
    try {
      // Check if user can access assessments using permission utility
      const canAccess = canUserAccessAssessments(user);

      // Fetch data based on permissions
      let assessmentsResponse: AssessmentsResponse;
      let suppliersResponse: SuppliersResponse;
      let contractsResponse: ContractsResponse;

      if (canAccess) {
        // Fetch all data including assessments
        const assessmentsParams = this.buildQueryParams({ limit: 1000, sortBy: 'updated_at', order: 'DESC' });
        const suppliersParams = this.buildQueryParams({ limit: 1 });
        const contractsParams = this.buildQueryParams({ limit: 1, status: 'Active', assessment_scope: 'Include' });

        [assessmentsResponse, suppliersResponse, contractsResponse] = await Promise.all([
          this.get<AssessmentsResponse>(`/assessments?${assessmentsParams}`),
          this.get<SuppliersResponse>(`/suppliers?${suppliersParams}`),
          this.get<ContractsResponse>(`/contracts?${contractsParams}`)
        ]);
      } else {
        // Only fetch suppliers and contracts when assessments are not accessible
        const suppliersParams = this.buildQueryParams({ limit: 1 });
        const contractsParams = this.buildQueryParams({ limit: 1, status: 'Active', assessment_scope: 'Include' });

        [suppliersResponse, contractsResponse] = await Promise.all([
          this.get<SuppliersResponse>(`/suppliers?${suppliersParams}`),
          this.get<ContractsResponse>(`/contracts?${contractsParams}`)
        ]);
        // Create empty assessments response
        assessmentsResponse = {
          data: [],
          total: 0,
          count: 0,
          statusTotals: {}
        };
      }

      // Calculate assessment statistics from the single assessments call
      const assessmentsByStatus: { [key: string]: number } = {};
      assessmentsResponse.data.forEach((assessment) => {
        const status = assessment.assessment_status;
        assessmentsByStatus[status] = (assessmentsByStatus[status] || 0) + 1;
      });

      const statusArray = Object.entries(assessmentsByStatus).map(([status, count]) => ({
        status,
        count
      }));

      // Get recent assessments from the same data (first 10 items)
      const recentAssessments = assessmentsResponse.data.slice(0, 10);

      return {
        assessments: {
          total: assessmentsResponse.total,
          byStatus: statusArray
        },
        suppliers: {
          total: suppliersResponse.total
        },
        contracts: {
          total: contractsResponse.total
        },
        recentAssessments: recentAssessments
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  }

  /**
   * Get Contract Role dashboard data
   */
  async getContractManagerDashboard(): Promise<ContractManagerDashboardResponse> {
    return this.get<ContractManagerDashboardResponse>('/contract-manager/dashboard');
  }

  /**
   * Get Contract Role gap items. The filters narrow the item lists only; totals and statistics
   * always cover the caller's whole scope.
   */
  async getContractManagerGapItems(filters?: GapItemsFilters): Promise<{
    contract_manager_id: number;
    total_gap_items: number;
    items_needing_evidence: number;
    gap_items: GapItem[];
    items_requiring_upload: GapItem[];
    evidence_statistics: EvidenceStatistics;
  }> {
    return this.get<{
      contract_manager_id: number;
      total_gap_items: number;
      items_needing_evidence: number;
      gap_items: GapItem[];
      items_requiring_upload: GapItem[];
      evidence_statistics: EvidenceStatistics;
    }>('/contract-manager/gap-items', filters);
  }

  /**
   * Get Executive dashboard data
   */
  async getExecutiveDashboard(): Promise<ExecutiveDashboardData> {
    return this.get<ExecutiveDashboardData>('/executive/dashboard');
  }

  async getProgramLeadMetrics(): Promise<ProgramLeadMetricsData> {
    return this.get<ProgramLeadMetricsData>('/program-lead/metrics');
  }

  async getProgramLeadQuickActions(): Promise<ProgramLeadQuickActionsData> {
    return this.get<ProgramLeadQuickActionsData>('/program-lead/quick-actions');
  }

  async getAdminQuickActions(): Promise<AdminQuickActionsData> {
    return this.get<AdminQuickActionsData>('/admin/quick-actions');
  }

  /** The Activated/Inactivated Contact-Users widget of the Admin Launch Page, the same data as on the Dashboard. */
  async getContactUserActivationWidgetData(): Promise<WidgetData> {
    return this.get<WidgetData>('/dashboard-widgets/contact-users/activation-trends');
  }

  /** The Contact-User Division Changes widget of the Admin Launch Page, the same data as on the Dashboard. */
  async getContactUserDivisionChangesWidgetData(): Promise<WidgetData> {
    return this.get<WidgetData>('/dashboard-widgets/contact-users/division-changes');
  }

  async getSchedulerMetrics(): Promise<SchedulerMetricsData> {
    return this.get<SchedulerMetricsData>('/scheduler/metrics');
  }

  async getSchedulerQuickActions(): Promise<SchedulerQuickActionsData> {
    return this.get<SchedulerQuickActionsData>('/scheduler/quick-actions');
  }

  async getSchedulerAssessorLoading(month: string): Promise<AssessorLoadingData> {
    return this.get<AssessorLoadingData>(assessorLoadingPath('/scheduler', month));
  }

  async getProgramLeadAssessorLoading(month: string): Promise<AssessorLoadingData> {
    return this.get<AssessorLoadingData>(assessorLoadingPath('/program-lead', month));
  }

  async getAssessorMetrics(): Promise<AssessorMetricsData> {
    return this.get<AssessorMetricsData>('/assessor/metrics');
  }

  async getAssessorQuickActions(): Promise<AssessorQuickActionsData> {
    return this.get<AssessorQuickActionsData>('/assessor/quick-actions');
  }

  async getContractRoleMetrics(): Promise<ContractRoleMetricsData> {
    return this.get<ContractRoleMetricsData>('/contract-role/metrics');
  }

  async getContractRoleQuickActions(): Promise<ContractRoleQuickActionsData> {
    return this.get<ContractRoleQuickActionsData>('/contract-role/quick-actions');
  }
}

export const dashboardService = new DashboardService();
export default dashboardService;
